import React, { useEffect, useState } from 'react';
import { IconButton, Link, Panel, Stack, Text, Toggle } from '@fluentui/react';
import IvicosStrings from 'kits/language/stringKit';
import { INTEGRATION_LABEL_STYLE } from '../integrationLayout';
import { OrganizationDto } from 'services/client-api-wrapper/identity_provider/responses.dto';
import { DocumensoDeactivationModal } from './DocumensoDeactivationModal';
import type { IDocumensoConfiguration } from './documenso.types';
import { DocumensoSettingsForm, resolveDocumensoErrorMessage } from './DocumensoSettingsForm';

const DOCUMENSO_WEBSITE_URL = 'https://documenso.com';

interface IDocumensoSettingsProps {
    isDocumensoEnabled: boolean;
    documensoBaseUrl: string;
    updateDocumensoConfig: (config: IDocumensoConfiguration) => Promise<OrganizationDto>;
    isOrgLoaded: boolean;
}

// Same flow as ZammadSettings: switching on opens a panel for the base URL, and the org is only
// configured once Save succeeds. Users then connect their own API token on the widget.
export const DocumensoSettings: React.FC<IDocumensoSettingsProps> = ({ isDocumensoEnabled, documensoBaseUrl, updateDocumensoConfig, isOrgLoaded }) => {
    const [isToggleOn, setIsToggleOn] = useState(isDocumensoEnabled);
    const [isSettingsPanelOpen, setIsSettingsPanelOpen] = useState(false);
    const [isDeactivationModalVisible, setIsDeactivationModalVisible] = useState(false);
    const [deactivationErrorMessage, setDeactivationErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        setIsToggleOn(isDocumensoEnabled);
    }, [isDocumensoEnabled]);

    const handleToggleChange = (_ev: React.MouseEvent<HTMLElement>, checked?: boolean): void => {
        setDeactivationErrorMessage(null);
        if (checked) {
            setIsToggleOn(true);
            setIsSettingsPanelOpen(true);
            return;
        }
        if (isDocumensoEnabled) {
            setIsDeactivationModalVisible(true);
        } else {
            setIsToggleOn(false);
        }
    };

    const handleConfirmDeactivate = async (): Promise<void> => {
        setIsDeactivationModalVisible(false);
        setDeactivationErrorMessage(null);
        try {
            await updateDocumensoConfig({});
        } catch (err) {
            console.error('Failed to disable Documenso integration:', err);
            setDeactivationErrorMessage(resolveDocumensoErrorMessage(err));
        }
    };

    return (
        <Stack tokens={{ childrenGap: 4 }}>
            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 12 }}>
                <Stack style={INTEGRATION_LABEL_STYLE}>
                    <Link
                        href={DOCUMENSO_WEBSITE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontWeight: 600 }}
                        data-test-id="documenso-website-link"
                    >
                        {IvicosStrings.documensoSettingsToggleLabel}
                    </Link>
                </Stack>
                <Stack.Item grow />
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                    <Toggle
                        checked={isToggleOn}
                        onText={IvicosStrings.providerEnabledLabel}
                        offText={IvicosStrings.providerDisabledLabel}
                        disabled={!isOrgLoaded}
                        onChange={handleToggleChange}
                        styles={{ root: { minWidth: 120, marginBottom: 0 } }}
                        data-test-id="enable-documenso-integration"
                    />
                    {isToggleOn && (
                        <>
                            <IconButton
                                iconProps={{ iconName: 'Settings' }}
                                onClick={() => setIsSettingsPanelOpen(true)}
                                data-test-id="documenso-settings-icon"
                            />
                            {isDocumensoEnabled ? (
                                <Text style={{ color: '#107C10', fontWeight: 600 }}>{IvicosStrings.documensoSettingsConfiguredStatus}</Text>
                            ) : (
                                <Text style={{ color: '#A80000', fontWeight: 600 }}>{IvicosStrings.documensoSettingsNotConfiguredStatus}</Text>
                            )}
                        </>
                    )}
                </Stack>
            </Stack>
            {deactivationErrorMessage && (
                <Text style={{ color: '#A80000' }} data-test-id="documenso-deactivation-error">
                    {deactivationErrorMessage}
                </Text>
            )}
            <Panel
                headerText={IvicosStrings.documensoSettingsToggleLabel}
                isOpen={isSettingsPanelOpen}
                onDismiss={() => setIsSettingsPanelOpen(false)}
                closeButtonAriaLabel="Close"
                data-test-id="documenso-settings-panel"
            >
                <DocumensoSettingsForm
                    documensoBaseUrl={documensoBaseUrl}
                    isOpen={isSettingsPanelOpen}
                    updateDocumensoConfig={updateDocumensoConfig}
                    onSaveSuccess={() => setIsSettingsPanelOpen(false)}
                    onCancel={() => setIsSettingsPanelOpen(false)}
                />
            </Panel>
            <DocumensoDeactivationModal
                isOpen={isDeactivationModalVisible}
                onCancel={() => setIsDeactivationModalVisible(false)}
                onConfirm={handleConfirmDeactivate}
            />
        </Stack>
    );
};
